import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useIsUserViewingSpaceAsAnonQuery$data as AnonymousAccessSpaceQueryType,
	useIsUserViewingSpaceAsAnonQuery$variables as AnonymousAccessSpaceQueryVariables,
} from './__generated__/useIsUserViewingSpaceAsAnonQuery.graphql.ts';

export const useIsUserViewingSpaceAsAnon = ({
	spaceKey,
	skipQuery,
}: {
	spaceKey: string;
	skipQuery?: boolean;
}) => {
	const { data, loading, error } = useQueryInterop<
		AnonymousAccessSpaceQueryType,
		AnonymousAccessSpaceQueryVariables
	>(
		graphql`
			query useIsUserViewingSpaceAsAnonQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
					currentUser {
						isAnonymouslyAuthorized
					}
				}
			}
		`,
		{
			variables: { spaceKey },
			skip: skipQuery || !spaceKey,
		},
	);

	// For restricted spaces the preloader's query returns a 403 which Apollo evicts from
	// its cache. This leaves `useQueryInterop` in a perpetual `loading: true` state during
	// SSR, while on the client the same query resolves with `loading: false` and
	// `space: null`. The resulting mismatch causes a hydration error in the SideNav footer.
	//
	// The preloader detects this case and persists the space key in a global
	// (`__RESTRICTED_SPACES_FROM_PRELOAD__`). When present, we short-circuit here so both
	// SSR and client agree on `{ loading: false, isUserViewingSpaceAsAnon: false }`.
	const restrictedSpaces =
		typeof window !== 'undefined'
			? (window as any).__RESTRICTED_SPACES_FROM_PRELOAD__
			: (globalThis as any).__RESTRICTED_SPACES_FROM_PRELOAD__;

	if (restrictedSpaces?.[spaceKey]) {
		return { isUserViewingSpaceAsAnon: false, loading: false, error: undefined };
	}

	return {
		isUserViewingSpaceAsAnon: Boolean(data?.space?.currentUser?.isAnonymouslyAuthorized),
		loading,
		error,
	};
};
